import BigNumber from 'bignumber.js';
import { useAtomValue } from 'jotai/index';
import { useCallback, useEffect } from 'react';
import {
  appAuthAtom,
  currencyExchangeRateRTAtom,
  CurrencyExchangeRates,
  inventoryDetailsAtom,
  Store,
} from '../../state';
import { CurrencyCross, CurrencyCrossObj, InstrumentCategoryEnum, InstrumentSubscriptionDataScope } from '../../types';
import { isDefined, isValid, Loadable } from '../../utils';
import { CurrencyExchangeRateRT } from './currencyExchangeRateRT';
import { SubscribeToInstrumentChangesFn } from './useSubscribeToInstrumentChanges';

const currencyExchangeRateRT = new CurrencyExchangeRateRT();

export const useRealtimeCurrencyUpdates = ({
  store,
  subscribeToInstrumentChanges,
}: {
  store: Store;
  subscribeToInstrumentChanges: SubscribeToInstrumentChangesFn;
}) => {
  const inventoryDetails = useAtomValue(inventoryDetailsAtom);
  const authState = useAtomValue(appAuthAtom);

  const subscribeToCurrencies = useCallback(
    (currencyCrosses: Array<CurrencyCrossObj>) => {
      if (currencyCrosses.length === 0) {
        return () => {};
      }

      const instruments = currencyExchangeRateRT.getRicListOfCurrencyCrosses(currencyCrosses).map(ric => ({
        ric,
        mic: '',
        instrumentCategory: InstrumentCategoryEnum.Currency,
      }));

      return subscribeToInstrumentChanges(
        {
          instruments,
          dataScope: [InstrumentSubscriptionDataScope.MarketData],
          source: 'inventory',
        },
        changes => {
          // collect every change we got from eStream as base/quote
          const changesMap: Record<CurrencyCross, number> = {};

          changes.forEach(change => {
            if (!isDefined(change.price) || !isDefined(change.price.value) || !isValid(change.price.value)) {
              return;
            }
            // The CURRENCY (change.ccy) field is always the quote currency, and PRIMACT_1 (change.price.value) is quote/base.
            // The pair's other (non-CURRENCY) currency is the base:
            //  - EUR= CURRENCY=USD  -> EURUSD= = value (1 EUR = value USD)
            //  - HUF= CURRENCY=HUF  -> USDHUF= = value (1 USD = value HUF)
            const { base, quote } = currencyExchangeRateRT.getBaseAndQuoteCurrency(change.ric);
            const [rateBase, rateQuote] = change.ccy === quote ? [base, quote] : [quote, base];
            const value = change.price.value;

            changesMap[`${rateBase}${rateQuote}=` as CurrencyCross] = value;
            changesMap[`${rateQuote}${rateBase}=` as CurrencyCross] = new BigNumber(1).div(value).toNumber();
          });

          const currencyExchangeRate = store.get(currencyExchangeRateRTAtom);

          const hasChanges = Object.keys(changesMap).find(currencyCross => {
            const rate = currencyExchangeRate[currencyCross as CurrencyCross];
            return rate && rate.isLoaded && rate.rateInHuf !== changesMap[currencyCross as CurrencyCross];
          });
          if (!hasChanges) {
            return;
          }

          store.set(currencyExchangeRateRTAtom, (curr: CurrencyExchangeRates) => {
            const updatedExchangeRates = { ...curr };
            Object.keys(changesMap).forEach(currencyCross => {
              updatedExchangeRates[currencyCross as CurrencyCross] = Loadable.loaded({
                currencyCross: currencyCross as CurrencyCross,
                rateInHuf: changesMap[currencyCross as CurrencyCross],
                lastUpdated: new Date().toISOString(),
              });
            });
            return updatedExchangeRates;
          });
        },
      );
    },
    [subscribeToInstrumentChanges],
  );

  useEffect(() => {
    if (authState.isAuthenticated && inventoryDetails.isLoaded) {
      return subscribeToCurrencies(inventoryDetails.currencyCrosses);
    } else {
      return () => {};
    }
  }, [authState, inventoryDetails, subscribeToCurrencies]);
};
